import type {
  ActionItem,
  ClientEmail,
  CreateShareRequest,
  CreateShareResponse,
  CustomAnswer,
  ExportToDriveResponse,
  FieldInsight,
  FieldInsightsResponse,
  InfographicImage,
  LiveDiagram,
  Meeting,
  MeetingSummary,
  RegenerateEmailRequest,
  Screenshot,
  ShareSuggestionsResponse,
  SuggestLabelsResponse,
  SuggestTitleResponse,
  SummaryTranslation,
  SummaryVersion,
  TranscriptLine,
  UpdateEmailRequest,
  UpsellOpportunity,
} from "@scoach/types";
import { getIdToken } from "../../lib/firebase.ts";
import { api } from "../../lib/http.ts";

const API_BASE =
  import.meta.env.VITE_API_BASE_URL === undefined
    ? "http://localhost:8080"
    : import.meta.env.VITE_API_BASE_URL;

export const summaryApi = {
  fetchSummary: (id: string) => api<MeetingSummary>(`/meetings/${id}/summary-data`),

  triggerSummarize: (id: string) =>
    api<{ jobId: string; status: string }>(`/meetings/${id}/summarize`, {
      method: "POST",
      body: {},
    }),

  /** Re-run the whole summary; snapshots the current one first. Returns the new
   * summary. Pass `technical` to re-run with a deeper technical deep-dive focus. */
  regenerateSummary: (id: string, opts?: { technical?: boolean }) =>
    api<MeetingSummary>(`/meetings/${id}/summary/regenerate`, {
      method: "POST",
      body: { technical: opts?.technical === true },
    }),

  /** List saved summary versions (newest first; no payload). */
  listVersions: (id: string) =>
    api<{ versions: Omit<SummaryVersion, "summary">[] }>(`/meetings/${id}/summary/versions`).then(
      (r) => r.versions,
    ),

  /** Read a previous version's payload WITHOUT restoring it (preview in place). */
  getVersion: (id: string, versionId: string) =>
    api<MeetingSummary>(`/meetings/${id}/summary/versions/${versionId}`),

  /** Restore a previous version as the current summary; returns it. */
  restoreVersion: (id: string, versionId: string) =>
    api<MeetingSummary>(`/meetings/${id}/summary/restore/${versionId}`, {
      method: "POST",
      body: {},
    }),

  suggestTitle: (id: string) =>
    api<SuggestTitleResponse>(`/meetings/${id}/suggest-title`, { method: "POST", body: {} }),

  renameMeeting: (id: string, title: string) =>
    api<Meeting>(`/meetings/${id}`, { method: "PATCH", body: { title, autoTitled: false } }),

  /** Reopen an ended meeting so the rep can continue from where it stopped. */
  resumeMeeting: (id: string) =>
    api<{ ok: boolean }>(`/meetings/${id}/resume`, { method: "POST", body: {} }),

  regenerateEmail: (id: string, body: RegenerateEmailRequest) =>
    api<ClientEmail>(`/meetings/${id}/email/regenerate`, { method: "POST", body }),

  updateEmail: (id: string, body: UpdateEmailRequest) =>
    api<ClientEmail>(`/meetings/${id}/email`, { method: "PATCH", body }),

  updateActionItems: (id: string, actionItems: ActionItem[]) =>
    api<ActionItem[]>(`/meetings/${id}/action-items`, { method: "PATCH", body: { actionItems } }),

  /** Persist client-computed custom Q&A answers (generated on the user's key). */
  saveCustomSummary: (id: string, answers: CustomAnswer[]) =>
    api<CustomAnswer[]>(`/meetings/${id}/custom-summary`, { method: "PUT", body: { answers } }),

  /** Answer the rep's custom questions SERVER-SIDE (Vertex + Apollo fallback) and
   * persist — works for every user without a personal Gemini key. */
  generateCustomSummary: (id: string, questions: string[]) =>
    api<CustomAnswer[]>(`/meetings/${id}/custom-summary/generate`, {
      method: "POST",
      body: { questions },
    }),

  /** Answer (server-side) the custom questions the transcript didn't cover, via Apollo.io. */
  apolloFallback: (id: string, questions: string[]) =>
    api<CustomAnswer[]>(`/meetings/${id}/custom-summary/apollo`, {
      method: "POST",
      body: { questions },
    }),

  /** List this meeting's infographic images via the API (works even when the browser
   * blocks client-side Firestore). */
  listInfographics: (id: string) =>
    api<{ items: InfographicImage[] }>(`/meetings/${id}/infographic-images-list`).then(
      (r) => r.items ?? [],
    ),

  /** List this meeting's live diagrams via the API (robust to blocked Firestore). */
  listDiagrams: (id: string) =>
    api<{ items: LiveDiagram[] }>(`/meetings/${id}/diagrams-list`).then((r) => r.items ?? []),

  /** List this meeting's screenshots via the API (robust to blocked Firestore). */
  listScreenshots: (id: string) =>
    api<{ items: Screenshot[] }>(`/meetings/${id}/screenshots-list`).then((r) => r.items ?? []),

  /** Generate an infographic for this meeting on demand (grounded in the transcript). */
  generateInfographic: (id: string) =>
    api<InfographicImage>(`/meetings/${id}/infographic`, { method: "POST", body: {} }),

  /** Translate the summary + client email into `lang` (cached server-side). */
  translate: (id: string, lang: string) =>
    api<SummaryTranslation>(`/meetings/${id}/translate`, { method: "POST", body: { lang } }),

  /** Model-suggested labels for this meeting (the user approves/edits before saving). */
  suggestLabels: (id: string) =>
    api<SuggestLabelsResponse>(`/meetings/${id}/suggest-labels`, { method: "POST", body: {} }).then(
      (r) => r.labels,
    ),

  /** Persist the meeting's labels (server sanitizes/dedupes). Returns the saved meeting. */
  updateLabels: (id: string, labels: string[]) =>
    api<Meeting>(`/meetings/${id}`, { method: "PATCH", body: { labels } }),

  /** Voice-of-the-Field product insights (bugs/features) for this meeting. */
  getFieldInsights: (id: string) => api<FieldInsightsResponse>(`/meetings/${id}/field-insights`),
  generateFieldInsights: (id: string) =>
    api<FieldInsightsResponse>(`/meetings/${id}/field-insights/generate`, {
      method: "POST",
      body: {},
    }),
  saveFieldInsights: (id: string, items: FieldInsight[]) =>
    api<FieldInsightsResponse>(`/meetings/${id}/field-insights`, {
      method: "PUT",
      body: { items },
    }),

  /** Persist manual edits to the meeting overview (narrative, attendees, participants, company, upsell). */
  updateOverview: (
    id: string,
    body: {
      summary?: string;
      attendees?: string[];
      participants?: string[];
      companyName?: string;
      upsell?: UpsellOpportunity[];
    },
  ) => api<MeetingSummary>(`/meetings/${id}/overview`, { method: "PATCH", body }),

  share: (id: string, body: CreateShareRequest) =>
    api<CreateShareResponse>(`/meetings/${id}/share`, { method: "POST", body }),

  /** People to auto-complete in the Share dialog: previously-shared + Sally users. */
  shareSuggestions: () =>
    api<ShareSuggestionsResponse>("/users/me/share-suggestions").then((r) => r.items),

  /**
   * Fetches the PDF as a Blob with the user's auth token. We don't use
   * `window.open` because that opens a new tab without the Authorization
   * header → Cloud Run 401s.
   */
  exportPdf: async (id: string): Promise<Blob> => {
    const token = await getIdToken().catch(() => null);
    const headers: Record<string, string> = {};
    if (token) headers["X-Authorization"] = `Bearer ${token}`;
    else if (import.meta.env.DEV) headers["X-Authorization"] = "Bearer dev-token";
    const res = await fetch(`${API_BASE}/meetings/${id}/summary.pdf`, { headers });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(
        `PDF export failed: HTTP ${res.status}${text ? ` — ${text.slice(0, 200)}` : ""}`,
      );
    }
    return res.blob();
  },

  /** Export this meeting's transcript + summary as a Markdown file to the user's
   * Google Drive. Throws ApiError with status 409 / code "drive-not-connected" when
   * the user hasn't connected Drive yet — the caller then runs the connect flow
   * (driveApi.connect) and retries. */
  exportToDrive: (id: string) =>
    api<ExportToDriveResponse>(`/meetings/${id}/export-to-drive`, { method: "POST", body: {} }),

  /** Raw transcript lines from the API (robust to client-side Firestore blocking). */
  fetchTranscript: (id: string) =>
    api<{ lines: TranscriptLine[] }>(`/meetings/${id}/transcript`).then((r) => r.lines ?? []),

  /** Download the raw transcript as a PDF. Uses fetch (not window.open) so the auth
   * header is sent. */
  exportTranscriptPdf: async (id: string): Promise<Blob> => {
    const token = await getIdToken().catch(() => null);
    const headers: Record<string, string> = {};
    if (token) headers["X-Authorization"] = `Bearer ${token}`;
    else if (import.meta.env.DEV) headers["X-Authorization"] = "Bearer dev-token";
    const res = await fetch(`${API_BASE}/meetings/${id}/transcript.pdf`, { headers });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(
        `Transcript PDF export failed: HTTP ${res.status}${text ? ` — ${text.slice(0, 200)}` : ""}`,
      );
    }
    return res.blob();
  },
};
